import { useMemo } from 'react';
import { useDebounce } from '@uidotdev/usehooks';
import { normalizeString } from 'src/utils/strings';
import { nodeToString } from '../utils/formatting/node.util';

export interface SearchableItem {
  content: React.ReactNode | string;
  value: string;
  /** Plain text for filtering rich option content. */
  textValue?: string;
  keywords?: string;
}

/**
 * Custom hook for filtering searchable items with debounced query
 * @param items - Array of searchable items
 * @param query - Current search query
 * @param debounceMs - Debounce delay in milliseconds
 * @returns Filtered items based on normalized search
 */
export const useSearchableFilter = <T extends SearchableItem>(
  items: T[],
  query: string,
  debounceMs = 300
): T[] => {
  const debouncedQuery = useDebounce(query, debounceMs);

  const filtered = useMemo(() => {
    const normalizedQuery = normalizeString(debouncedQuery);

    if (!normalizedQuery) {
      return items;
    }
    return items.filter((item) => {
      const normalizedLabel = normalizeString(
        item.textValue ?? nodeToString(item.content)
      );
      const normalizedKeywords = normalizeString(item.keywords ?? '');

      const labelMatches = normalizedLabel.includes(normalizedQuery);
      const keywordsMatch = normalizedKeywords.includes(normalizedQuery);

      return labelMatches || keywordsMatch;
    });
  }, [items, debouncedQuery]);

  return filtered;
};
